import { MultiSelectOption } from "@/components/ui/multi-select";

import { formatList, formatToHumanReadable } from "@/lib/format-data";

import type { CarePlanCycleDetail } from "@/types/care-plan";
import { ServiceDetail } from "@/types/service";
import { type TaskPriority } from "@/types/task";

import type { SequenceDraft } from "./sequences/types";

// ============================================================================
// Step Types
// ============================================================================

export enum StepType {
  APPOINTMENT = "APPOINTMENT",
  TASK = "TASK",
  MESSAGE = "MESSAGE",
  DECISION = "DECISION",
}

export enum MessageTemplateType {
  REMINDER = "REMINDER",
  CONFIRMATION = "CONFIRMATION",
  FOLLOW_UP = "FOLLOW_UP",
  CANCELLED = "CANCELLED",
  OVERRIDE = "OVERRIDE",
}

export interface CarePlanStepBase {
  id: string;
  templateStepId?: string;
  stepNumber: number;
  type: StepType;
  serviceIds: string[];
  medicationIds: string[];
  measurements: {
    id?: string;
    measurementField: string;
    displayUnit?: string;
  }[];
  description?: string;
  minutesAfterPrevious?: number;
  // Task step fields
  taskTitle?: string;
  taskPriority?: TaskPriority;
  taskAssigneeId?: string;
  taskTeamId?: string;
  taskDescription?: string;
  taskDueDateMinutes?: number;
  // Message step fields
  smsBody?: string;
  messageType?: MessageTemplateType;
  /** Links auto-generated message step to its parent appointment step */
  parentStepId?: string;
  /** Which service's templates were used to generate this message */
  sourceServiceId?: string;
  branchId?: string;
  branches?: CarePlanBranch[];
  // Cycle fields
  cycleId?: string | null;
  cyclePass?: number | null;
  sourceStepId?: string | null;
  // Step-level sequences. Populated from
  // CarePlanStepDetail.sequences / CarePlanTemplateStepDetail.sequences.
  sequences?: SequenceDraft[];
  // In-memory only: set by the sequences provider when add/update/remove
  // mutates the list, consumed by the save serializer to decide between
  // `undefined` (leave alone) and the array (reconcile). Never persisted.
  sequencesDirty?: boolean;
}

export interface TemplateStep extends CarePlanStepBase {
  kind: "template";
}

export enum CarePlanLogAction {
  CREATED = "CREATED",
  REMOVED = "REMOVED",
  FIELD_CHANGED = "FIELD_CHANGED",
  STATUS_CHANGED = "STATUS_CHANGED",
  CYCLE_CREATED = "CYCLE_CREATED",
  CYCLE_UPDATED = "CYCLE_UPDATED",
  CYCLE_REMOVED = "CYCLE_REMOVED",
}

export enum CarePlanLogSource {
  USER = "USER",
  PATIENT_SELF_SCHEDULE = "PATIENT_SELF_SCHEDULE",
  EVENT_SYNC = "EVENT_SYNC",
  TASK_SYNC = "TASK_SYNC",
  BRANCH_SELECTION = "BRANCH_SELECTION",
  CYCLE_EDIT_THIS = "CYCLE_EDIT_THIS",
  CYCLE_EDIT_FUTURE = "CYCLE_EDIT_FUTURE",
  CYCLE_EDIT_ALL = "CYCLE_EDIT_ALL",
  CYCLE_PROPAGATION = "CYCLE_PROPAGATION",
  CYCLE_MATERIALIZATION = "CYCLE_MATERIALIZATION",
}

export enum CarePlanLogField {
  NAME = "NAME",
  DESCRIPTION = "DESCRIPTION",
  START_DATE = "START_DATE",
  TAG_IDS = "TAG_IDS",
  LOCATION_ID = "LOCATION_ID",
  ASSIGNED_BY_ID = "ASSIGNED_BY_ID",
  STEP_NUMBER = "STEP_NUMBER",
  STEP_TYPE = "STEP_TYPE",
  STEP_DESCRIPTION = "STEP_DESCRIPTION",
  MESSAGE_TYPE = "MESSAGE_TYPE",
  MINUTES_AFTER_PREVIOUS = "MINUTES_AFTER_PREVIOUS",
  PARENT_STEP_ID = "PARENT_STEP_ID",
  SOURCE_SERVICE_ID = "SOURCE_SERVICE_ID",
  SCHEDULED_DATE = "SCHEDULED_DATE",
  STATUS = "STATUS",
  EVENT_ID = "EVENT_ID",
  TASK_ID = "TASK_ID",
  TASK_TITLE = "TASK_TITLE",
  TASK_PRIORITY = "TASK_PRIORITY",
  TASK_ASSIGNEE_ID = "TASK_ASSIGNEE_ID",
  TASK_TEAM_ID = "TASK_TEAM_ID",
  TASK_DESCRIPTION = "TASK_DESCRIPTION",
  TASK_DUE_DATE = "TASK_DUE_DATE",
  TASK_START_DATE = "TASK_START_DATE",
  TASK_STATUS = "TASK_STATUS",
  SERVICE_IDS = "SERVICE_IDS",
  MEDICATION_IDS = "MEDICATION_IDS",
  MEASUREMENTS = "MEASUREMENTS",
  SMS_BODY = "SMS_BODY",
  BRANCH_ID = "BRANCH_ID",
  BRANCH_SELECTED = "BRANCH_SELECTED",
  BRANCH_NAME = "BRANCH_NAME",
  BRANCH_POSITION = "BRANCH_POSITION",
  BRANCH_TAG_IDS = "BRANCH_TAG_IDS",
  CYCLE_PASS_COUNT = "CYCLE_PASS_COUNT",
  CYCLE_GAP_MINUTES = "CYCLE_GAP_MINUTES",
}

export enum CarePlanStepStatus {
  PENDING = "PENDING",
  SCHEDULED = "SCHEDULED",
  COMPLETED = "COMPLETED",
  SKIPPED = "SKIPPED",
  MISSED = "MISSED",
}

export interface PatientCarePlanStep extends CarePlanStepBase {
  kind: "patient";
  scheduledDate: string; // ISO datetime, REQUIRED
  status: CarePlanStepStatus;
  eventId?: string;
  taskId?: string;
  completedDate?: string;
  isDateEstimated?: boolean;
}

export type CarePlanStep = TemplateStep | PatientCarePlanStep;

export interface CarePlanBranch {
  id: string;
  decisionStepId: string;
  templateBranchId?: string;
  name?: string;
  position: number;
  tagIds: string[];
  selectedDate?: string;
  selectedById?: string;
  steps: CarePlanStep[];
}

export function isPatientStep(
  step: CarePlanStep | CarePlanStepBase,
): step is PatientCarePlanStep {
  return "kind" in step && step.kind === "patient";
}

/**
 * Returns true for auto-generated message steps linked to a parent appointment step.
 * Standalone message steps (created manually, no parentStepId) return false.
 */
export function isLinkedMessage<T extends CarePlanStep | CarePlanStepBase>(
  step: T,
): step is T & { parentStepId: string } {
  return step.type === StepType.MESSAGE && !!step.parentStepId;
}

/**
 * Map API step detail objects to PatientCarePlanStep shape.
 * Centralised to avoid duplication across pages that display patient steps.
 */
export function toPatientSteps(
  steps: {
    id: string;
    stepNumber: number;
    type: StepType;
    messageType?: string;
    description?: string;
    minutesAfterPrevious?: number;
    parentStepId?: string;
    sourceServiceId?: string;
    serviceIds: string[];
    medicationIds: string[];
    measurements: { measurementField: string; displayUnit?: string }[];
    smsBody?: string;
    taskTitle?: string;
    taskPriority?: string;
    taskAssigneeId?: string;
    taskTeamId?: string;
    taskDescription?: string;
    taskDueDateMinutes?: number;
    eventId?: string;
    taskId?: string;
    branchId?: string;
    branches?: any[];
    cycleId?: string | null;
    cyclePass?: number | null;
    sourceStepId?: string | null;
    scheduledDate: string;
    status: CarePlanStepStatus;
    completedDate?: string;
    isDateEstimated?: boolean;
    sequences?: {
      id: string;
      steps: {
        id: string;
        delayMinutes: number;
        effectType: string;
        config?: Record<string, unknown> | null;
        isActive: boolean;
      }[];
    }[];
  }[],
): PatientCarePlanStep[] {
  return steps.map((s) => ({
    kind: "patient" as const,
    id: s.id,
    stepNumber: s.stepNumber,
    type: s.type,
    messageType: s.messageType as MessageTemplateType | undefined,
    description: s.description,
    minutesAfterPrevious: s.minutesAfterPrevious,
    parentStepId: s.parentStepId,
    sourceServiceId: s.sourceServiceId,
    serviceIds: s.serviceIds,
    medicationIds: s.medicationIds,
    measurements: s.measurements,
    smsBody: s.smsBody,
    taskTitle: s.taskTitle,
    taskPriority: s.taskPriority as TaskPriority | undefined,
    taskAssigneeId: s.taskAssigneeId,
    taskTeamId: s.taskTeamId,
    taskDescription: s.taskDescription,
    taskDueDateMinutes: s.taskDueDateMinutes,
    eventId: s.eventId,
    taskId: s.taskId,
    branchId: s.branchId ?? undefined,
    cycleId: s.cycleId ?? null,
    cyclePass: s.cyclePass ?? null,
    sourceStepId: s.sourceStepId ?? null,
    branches:
      s.branches?.map((b: any) => ({
        id: b.id,
        decisionStepId: b.decision_step_id ?? b.decisionStepId,
        templateBranchId: b.template_branch_id ?? b.templateBranchId,
        name: b.name,
        position: b.position,
        tagIds: b.tag_ids ?? b.tagIds ?? [],
        selectedDate: b.selected_date ?? b.selectedDate,
        selectedById: b.selected_by_id ?? b.selectedById,
        steps: toPatientSteps(b.steps ?? []),
      })) ?? undefined,
    scheduledDate: s.scheduledDate,
    status: s.status,
    completedDate: s.completedDate,
    isDateEstimated: s.isDateEstimated,
    sequences: toSequenceDrafts(s.sequences ?? []),
  }));
}

/**
 * Flatten the API's `sequences[]` (one wrapper sequence per care-plan step)
 * into a flat list of `SequenceDraft` rows for the editor.
 */
export function toSequenceDrafts(
  sequences: {
    steps: {
      id: string;
      delayMinutes: number;
      effectType: string;
      config?: Record<string, unknown> | null;
      isActive: boolean;
    }[];
  }[],
): SequenceDraft[] {
  return sequences.flatMap((seq) =>
    seq.steps.map((step) => {
      const effect = (step.config?.effect ?? {}) as Record<string, unknown>;
      const effectType =
        step.effectType === "SEND_EMAIL" ? "SEND_EMAIL" : "SEND_SMS";
      return {
        key: step.id,
        id: step.id,
        delayMinutes: step.delayMinutes,
        effectType,
        smsBody:
          effectType === "SEND_SMS"
            ? (effect.template as string | undefined)
            : undefined,
        emailBody:
          effectType === "SEND_EMAIL"
            ? (effect.template as string | undefined)
            : undefined,
        emailSubject:
          effectType === "SEND_EMAIL"
            ? (effect.subject as string | undefined)
            : undefined,
        emailTemplateJson:
          effectType === "SEND_EMAIL"
            ? (effect.template_json as SequenceDraft["emailTemplateJson"])
            : undefined,
        isActive: step.isActive,
      } as SequenceDraft;
    }),
  );
}

// ============================================================================
// Message Template Configuration
// ============================================================================

export interface MessageTemplateConfig {
  type: MessageTemplateType;
  label: string;
  /** "before" = insert before the appointment step, "after" = insert after */
  placement: "before" | "after";
}

/**
 * Ordered by chronological placement in the timeline:
 *   [Reminder] → [Appointment] → [Confirmation] → [Follow-up]
 *
 * "before" messages are inserted before the appointment step.
 * "after" messages are inserted after, in the order listed here.
 */
export const MESSAGE_TEMPLATE_CONFIGS: MessageTemplateConfig[] = [
  {
    type: MessageTemplateType.REMINDER,
    label: "Reminder",
    placement: "before",
  },
  {
    type: MessageTemplateType.CONFIRMATION,
    label: "Confirmation",
    placement: "after",
  },
  {
    type: MessageTemplateType.FOLLOW_UP,
    label: "Follow-up",
    placement: "after",
  },
];

// ============================================================================
// Computed Step Name
// ============================================================================

const NOTE_SNIPPET_LENGTH = 50;

export function getStepDisplayName(
  step: CarePlanStep,
  lookups: {
    serviceById: Map<string, ServiceDetail>;
    medicationById: Map<string, MultiSelectOption>;
    measurementById: Map<string, MultiSelectOption>;
  },
): string {
  if (step.type === StepType.DECISION) {
    return "Decision Point";
  }

  // Task steps prefer their explicit title
  if (step.type === StepType.TASK && step.taskTitle) {
    return step.taskTitle;
  }

  // Message steps show their template type label (linked) or body preview (standalone)
  if (step.type === StepType.MESSAGE) {
    if (step.messageType) {
      const label = formatToHumanReadable(step.messageType);
      const bodyPreview = step.smsBody?.slice(0, 40);
      if (bodyPreview) {
        const ellipsis = (step.smsBody?.length ?? 0) > 40 ? "…" : "";
        return `${label}: ${bodyPreview}${ellipsis}`;
      }
      return `${label} SMS`;
    }
    // Standalone message — show body preview
    if (step.smsBody?.trim()) {
      const trimmed = step.smsBody.trim();
      return trimmed.length > NOTE_SNIPPET_LENGTH
        ? `${trimmed.slice(0, NOTE_SNIPPET_LENGTH)}…`
        : trimmed;
    }
    return "Message";
  }

  // 1. Services take precedence
  if (step.serviceIds.length > 0) {
    const names = step.serviceIds
      .map((id) => lookups.serviceById.get(id)?.name)
      .filter(Boolean) as string[];
    if (names.length > 0) return formatList(names);
  }

  // 2. Medications
  if (step.medicationIds.length > 0) {
    const names = step.medicationIds
      .map((id) => lookups.medicationById.get(id)?.label)
      .filter(Boolean) as string[];
    if (names.length > 0) return `Administer ${formatList(names)}`;
  }

  // 3. Measurements
  if (step.measurements.length > 0) {
    const names = step.measurements
      .map((m) => lookups.measurementById.get(m.measurementField)?.label)
      .filter(Boolean) as string[];
    if (names.length > 0) return `Measure ${formatList(names)}`;
  }

  // 4. Note snippet
  if (step.description?.trim()) {
    const trimmed = step.description.trim();
    return trimmed.length > NOTE_SNIPPET_LENGTH
      ? `${trimmed.slice(0, NOTE_SNIPPET_LENGTH)}…`
      : trimmed;
  }

  return "";
}

/**
 * A step is "empty" when it has no meaningful content configured.
 * Does not depend on lookup maps — only checks raw field presence.
 */
export function isStepEmpty(step: CarePlanStep): boolean {
  if (step.type === StepType.DECISION) return false;
  if (step.type === StepType.TASK && step.taskTitle) return false;
  if (
    step.type === StepType.MESSAGE &&
    (step.messageType || step.smsBody?.trim())
  )
    return false;
  if (step.serviceIds?.length > 0) return false;
  if (step.medicationIds?.length > 0) return false;
  if (step.measurements?.length > 0) return false;
  if (step.description?.trim()) return false;
  return true;
}

export function isDecisionResolved(step: CarePlanStep): boolean {
  return (step.branches ?? []).some((b) => !!b.selectedDate);
}

/**
 * Recursively locate a branch by ID anywhere in the step tree.
 * Returns the branch and its parent decision step, or null if not found.
 */
export function findBranchInTree(
  steps: CarePlanStep[],
  branchId: string,
): { branch: CarePlanBranch; decisionStep: CarePlanStep } | null {
  for (const step of steps) {
    if (step.type !== StepType.DECISION || !step.branches) continue;
    for (const branch of step.branches) {
      if (branch.id === branchId) return { branch, decisionStep: step };
      const nested = findBranchInTree(branch.steps, branchId);
      if (nested) return nested;
    }
  }
  return null;
}

export function findStep(
  steps: CarePlanStep[],
  id: string,
): CarePlanStep | null {
  for (const step of steps) {
    if (step.id === id) return step;
    if (step.type === StepType.DECISION) {
      for (const branch of step.branches ?? []) {
        const found = findStep(branch.steps, id);
        if (found) return found;
      }
    }
  }
  return null;
}

/**
 * Locate a decision step in the tree and return its siblings as a timeline
 * (MESSAGE steps excluded), plus the decision's index in that timeline.
 */
export function findDecisionTimelineContext(
  stepList: CarePlanStep[],
  decisionStepId: string,
): { timelineSteps: CarePlanStep[]; index: number } | null {
  const timelineSteps = stepList.filter((s) => !isLinkedMessage(s));
  const index = timelineSteps.findIndex((s) => s.id === decisionStepId);
  if (index !== -1) {
    return { timelineSteps, index };
  }
  for (const step of stepList) {
    if (step.type === StepType.DECISION && step.branches) {
      for (const branch of step.branches) {
        const found = findDecisionTimelineContext(branch.steps, decisionStepId);
        if (found) return found;
      }
    }
  }
  return null;
}

/** True when the decision has at least one following non-MESSAGE step in the same list. */
export function hasTimelineStepAfterDecision(
  steps: CarePlanStep[],
  decisionStepId: string,
): boolean {
  const ctx = findDecisionTimelineContext(steps, decisionStepId);
  if (!ctx) return false;
  return ctx.index < ctx.timelineSteps.length - 1;
}

/**
 * Recursively locate a branch by ID within the step tree and apply a
 * mutation function to it. Returns a new step array with the branch
 * replaced. If the branch is not found, the original array is returned
 * by reference (no copy).
 */
export function updateBranchInTree<T extends CarePlanStep>(
  steps: T[],
  branchId: string,
  mutate: (branch: CarePlanBranch, decisionStep: T) => CarePlanBranch,
): T[] {
  return steps.map((step) => {
    if (step.type !== StepType.DECISION) return step;
    const branches = step.branches ?? [];

    const branchIdx = branches.findIndex((b) => b.id === branchId);
    if (branchIdx !== -1) {
      const updatedBranches = [...branches];
      updatedBranches[branchIdx] = mutate(branches[branchIdx], step);
      return { ...step, branches: updatedBranches };
    }

    let changed = false;
    const updatedBranches = branches.map((branch) => {
      const updatedSteps = updateBranchInTree(
        branch.steps,
        branchId,
        mutate as (
          branch: CarePlanBranch,
          decisionStep: CarePlanStep,
        ) => CarePlanBranch,
      );
      if (updatedSteps !== branch.steps) {
        changed = true;
        return { ...branch, steps: updatedSteps };
      }
      return branch;
    });

    return changed ? { ...step, branches: updatedBranches } : step;
  });
}

/**
 * Recursively remove a step (and its child message steps) from anywhere in
 * the tree — top-level or inside branches. Returns a new step array with
 * the step removed, or the original array by reference if nothing changed.
 */
export function removeStepFromTree<T extends CarePlanStep>(
  steps: T[],
  stepId: string,
): T[] {
  // Try top-level removal first
  const hasTopLevel = steps.some(
    (s) => s.id === stepId || s.parentStepId === stepId,
  );
  if (hasTopLevel) {
    return steps.filter(
      (s) => s.id !== stepId && s.parentStepId !== stepId,
    ) as T[];
  }

  // Otherwise look inside branches
  let changed = false;
  const result = steps.map((step) => {
    if (step.type !== StepType.DECISION) return step;
    const branches = step.branches ?? [];
    let branchesChanged = false;
    const updatedBranches = branches.map((branch) => {
      const updatedSteps = removeStepFromTree(branch.steps, stepId);
      if (updatedSteps !== branch.steps) {
        branchesChanged = true;
        return { ...branch, steps: updatedSteps };
      }
      return branch;
    });
    if (branchesChanged) {
      changed = true;
      return { ...step, branches: updatedBranches } as T;
    }
    return step;
  });
  return changed ? result : steps;
}

/**
 * Collect all step IDs within a step tree (the step itself + all branch descendants).
 */
export function collectAllStepIds(step: CarePlanStep): Set<string> {
  const ids = new Set<string>([step.id]);
  if (step.branches) {
    for (const branch of step.branches) {
      for (const s of branch.steps) {
        collectAllStepIds(s).forEach((id) => ids.add(id));
      }
    }
  }
  return ids;
}

/**
 * Recursively remap cycleId on a step and all its branch descendants.
 */
export function remapCycleIds<T extends CarePlanStep>(
  step: T,
  cycleIdMap: Map<string, string>,
): T {
  let updated = step;
  if (step.cycleId && cycleIdMap.has(step.cycleId)) {
    updated = { ...updated, cycleId: cycleIdMap.get(step.cycleId)! };
  }
  if (updated.branches) {
    updated = {
      ...updated,
      branches: updated.branches.map((branch) => ({
        ...branch,
        steps: branch.steps.map((s) => remapCycleIds(s, cycleIdMap)),
      })),
    };
  }
  return updated;
}

/**
 * Build a mapping from old branch IDs to new branch IDs by walking
 * original and cloned step trees in parallel (DFS).
 */
export function buildBranchIdMap(
  original: CarePlanStep,
  cloned: CarePlanStep,
): Map<string, string> {
  const map = new Map<string, string>();
  if (original.branches && cloned.branches) {
    for (let i = 0; i < original.branches.length; i++) {
      map.set(original.branches[i].id, cloned.branches[i].id);
      for (let j = 0; j < original.branches[i].steps.length; j++) {
        buildBranchIdMap(
          original.branches[i].steps[j],
          cloned.branches[i].steps[j],
        ).forEach((v, k) => map.set(k, v));
      }
    }
  }
  return map;
}

/**
 * Find cycles where ALL steps with that cycleId are within the given step's tree.
 * These cycles are "self-contained" and should be duplicated when the step is duplicated.
 */
export function findSelfContainedCycles(
  step: CarePlanStep,
  allSteps: CarePlanStep[],
  cycles: CarePlanCycleDetail[],
): CarePlanCycleDetail[] {
  if (cycles.length === 0) return [];

  const descendantIds = collectAllStepIds(step);

  // Build map: cycleId → original (pass-1) step IDs with that cycleId.
  // Skip materialized copies (sourceStepId set) — they live outside the
  // original step tree and would incorrectly break the self-containment check.
  const cycleStepMap = new Map<string, Set<string>>();
  const walk = (steps: CarePlanStep[]) => {
    for (const s of steps) {
      if (s.cycleId && !s.sourceStepId) {
        const set = cycleStepMap.get(s.cycleId) ?? new Set<string>();
        set.add(s.id);
        cycleStepMap.set(s.cycleId, set);
      }
      if (s.branches) {
        for (const b of s.branches) walk(b.steps);
      }
    }
  };
  walk(allSteps);

  return cycles.filter((cycle) => {
    const stepsInCycle = cycleStepMap.get(cycle.id);
    if (!stepsInCycle || stepsInCycle.size === 0) return false;
    return Array.from(stepsInCycle).every((id) => descendantIds.has(id));
  });
}

/**
 * For a self-contained cycle within a step tree, find where its steps live:
 * either on the step itself (top-level) or within a specific branch (DFS).
 * Returns the branch ID and the non-message step index range, or null.
 */
export function locateCycleInStep(
  step: CarePlanStep,
  cycleId: string,
): { branchId: string | null; fromIndex: number; toIndex: number } | null {
  // The step itself has the cycleId → top-level
  if (step.cycleId === cycleId) {
    return { branchId: null, fromIndex: -1, toIndex: -1 };
  }

  if (!step.branches) return null;

  for (const branch of step.branches) {
    // Check non-message steps in this branch
    const timeline = branch.steps.filter((s) => !isLinkedMessage(s));
    let from = -1;
    let to = -1;
    for (let i = 0; i < timeline.length; i++) {
      if (timeline[i].cycleId === cycleId) {
        if (from === -1) from = i;
        to = i;
      }
    }
    if (from >= 0) {
      return { branchId: branch.id, fromIndex: from, toIndex: to };
    }

    // DFS into nested decision steps within this branch
    for (const branchStep of branch.steps) {
      const nested = locateCycleInStep(branchStep, cycleId);
      if (nested) return nested;
    }
  }

  return null;
}

/**
 * Re-scope cycle membership after a tree mutation (e.g. drag-and-drop).
 *
 * A cycle is "anchored" to a container — `cycle.branchId` (null = root). Its
 * primaries live at the top of that container; any DECISION primary
 * propagates `cycleId` into its entire branch subtree (cf. `stampSubtree`).
 *
 * After a move we need both directions:
 *  - a step dragged OUT of a cycled subtree keeps a stale `cycleId` that has
 *    to be cleared, and
 *  - a step dragged INTO a cycled subtree should pick up that cycle's id so
 *    it actually belongs to the cycle the user can see around it.
 *
 * Algorithm:
 *  - Identify pre-move primaries from `prevSteps` (top-of-home steps whose
 *    cycleId matches a cycle, skipping materialized clones).
 *  - For each cycle, compute its index range in the new home container —
 *    [first..last] of surviving primaries and materialized clones. This is
 *    the cycle's visible scope: from its first step through the last step
 *    of its last pass. Steps that fall inside the range (peers between
 *    primaries, or between pass 1 and pass 2 clones) are scope members and
 *    inherit the cycle id, even if they were just dragged in.
 *  - Walk `nextSteps` carrying an "ambient" cycleId — the nearest cycled
 *    ancestor decision's id while descending. For each step:
 *      • If it's still in its prev-primary cycle's home container, it stays
 *        a primary and contributes that cycleId to descendants.
 *      • Else if its index lies inside a container-anchored cycle's scope
 *        range, it adopts that cycle id (and propagates to its branches).
 *      • Otherwise its expected cycleId is whatever the ambient is (so
 *        anything dropped inside a cycled subtree inherits, anything dropped
 *        outside any cycled subtree gets stripped).
 *      • Materialized clones (`sourceStepId` set) are pass 2+ subtrees with
 *        their own scoping — skip and don't recurse.
 *  - Cycles with no surviving primary are returned in `removedCycleIds` so
 *    the caller can drop them from the cycles list.
 */
export function reconcileCycleScope(
  prevSteps: CarePlanStep[],
  nextSteps: CarePlanStep[],
  cycles: CarePlanCycleDetail[],
): { steps: CarePlanStep[]; removedCycleIds: string[] } {
  if (cycles.length === 0) return { steps: nextSteps, removedCycleIds: [] };

  const cycleHome = new Map<string, string | null>();
  for (const cycle of cycles) cycleHome.set(cycle.id, cycle.branchId);

  const prevPrimary = new Map<string, string>();
  for (const cycle of cycles) {
    const prevHome =
      cycle.branchId === null
        ? prevSteps
        : (findBranchInTree(prevSteps, cycle.branchId)?.branch.steps ?? null);
    if (!prevHome) continue;
    for (const s of prevHome) {
      if (s.cycleId === cycle.id && !s.sourceStepId) {
        prevPrimary.set(s.id, cycle.id);
      }
    }
  }

  const survivingPrimaries = new Set<string>();
  let mutated = false;

  /** Cycle id this step is a surviving primary for, scoped to the container
   * we're currently descending into — or null. A surviving primary is a
   * prev-primary whose cycle is still anchored to its original container. */
  const survivingPrimaryCycleId = (
    step: CarePlanStep,
    currentBranchId: string | null,
  ): string | null => {
    const cid = prevPrimary.get(step.id);
    return cid && cycleHome.get(cid) === currentBranchId ? cid : null;
  };

  function descend<T extends CarePlanStep>(
    arr: T[],
    ambientCycleId: string | null,
    currentBranchId: string | null,
  ): T[] {
    // Pre-compute index range [first..last] of each cycle anchored to this
    // container, considering only legitimate members (surviving prev-primary
    // OR materialized clone). Stale cycleIds carried by a step dragged out
    // of a branch into this container are deliberately excluded so they
    // don't bogusly expand the range.
    const cycleRanges = new Map<string, { first: number; last: number }>();
    for (const cycle of cycles) {
      if (cycle.branchId !== currentBranchId) continue;
      let first = -1;
      let last = -1;
      for (let i = 0; i < arr.length; i++) {
        const s = arr[i];
        const isPrimary =
          survivingPrimaryCycleId(s, currentBranchId) === cycle.id;
        const isClone = !!s.sourceStepId && s.cycleId === cycle.id;
        if (isPrimary || isClone) {
          if (first === -1) first = i;
          last = i;
        }
      }
      if (first !== -1) cycleRanges.set(cycle.id, { first, last });
    }

    let changed = false;
    const next = arr.map((step, idx) => {
      // Materialized clones (pass 2+) carry their own cycleId by design —
      // leave the entire subtree alone.
      if (step.sourceStepId) return step;

      const primaryCycleId = survivingPrimaryCycleId(step, currentBranchId);
      if (primaryCycleId) survivingPrimaries.add(primaryCycleId);

      // Container-scope membership: a peer step that lands inside a cycle's
      // visible range adopts its cycle id. The first matching cycle wins —
      // overlapping ranges within the same container are an existing edge
      // case and not in scope here.
      let scopeCycleId: string | null = null;
      if (primaryCycleId === null) {
        for (const [cid, { first, last }] of Array.from(cycleRanges)) {
          if (idx >= first && idx <= last) {
            scopeCycleId = cid;
            break;
          }
        }
      }

      const expectedCycleId = primaryCycleId ?? scopeCycleId ?? ambientCycleId;

      let updated = step;
      if (step.branches) {
        const newBranches = step.branches.map((b) => {
          const newSteps = descend(b.steps, expectedCycleId, b.id);
          return newSteps === b.steps ? b : { ...b, steps: newSteps };
        });
        if (newBranches.some((b, i) => b !== step.branches![i])) {
          updated = { ...updated, branches: newBranches } as T;
        }
      }
      if (updated.cycleId !== expectedCycleId) {
        if (expectedCycleId === null) {
          updated = { ...updated, cycleId: null, cyclePass: null } as T;
        } else {
          const pass = updated.cyclePass ?? 1;
          updated = {
            ...updated,
            cycleId: expectedCycleId,
            cyclePass: pass,
          } as T;
        }
      }
      if (updated !== step) changed = true;
      return updated;
    });
    if (changed) mutated = true;
    return changed ? next : arr;
  }

  const newSteps = descend(nextSteps, null, null);
  const removedCycleIds = cycles
    .filter((c) => !survivingPrimaries.has(c.id))
    .map((c) => c.id);
  return {
    steps: mutated ? newSteps : nextSteps,
    removedCycleIds,
  };
}

/**
 * Drop the listed cycles from the list and reparent any surviving cycle whose
 * `parentCycleId` pointed at a removed cycle to the nearest non-removed
 * ancestor (or null).  A move that destroys an outer cycle but leaves an
 * inner one intact would otherwise leave the inner cycle holding a dangling
 * `parentCycleId`, which the save payload's parent-chain validation rejects.
 */
export function removeCyclesAndReparent(
  cycles: CarePlanCycleDetail[],
  removedIds: string[],
): CarePlanCycleDetail[] {
  if (removedIds.length === 0) return cycles;
  const removed = new Set(removedIds);
  const cycleById = new Map(cycles.map((c) => [c.id, c]));
  const resolveParent = (id: string | null): string | null => {
    let cur = id;
    while (cur && removed.has(cur)) {
      cur = cycleById.get(cur)?.parentCycleId ?? null;
    }
    return cur;
  };
  return cycles
    .filter((c) => !removed.has(c.id))
    .map((c) => {
      if (c.parentCycleId && removed.has(c.parentCycleId)) {
        return { ...c, parentCycleId: resolveParent(c.parentCycleId) };
      }
      return c;
    });
}

/** Info about a cycle that was duplicated along with its step. */
export interface DuplicatedCycleInfo {
  cycle: CarePlanCycleDetail;
  fromStepId: string;
  toStepId: string;
  branchId: string | null;
}

// ============================================================================
// Drag-and-drop containers
// ============================================================================

/**
 * Identifier for a sortable container in the care-plan tree.
 *
 * - "root" — the top-level step list
 * - `branch:${branchId}` — the steps array of a specific branch
 *
 * Used by the unified moveStepTo action so a single drag-end handler can
 * dispatch both same-container reorders and cross-container moves.
 */
export type StepContainerId = "root" | `branch:${string}`;

export function rootContainer(): StepContainerId {
  return "root";
}

export function branchContainer(branchId: string): StepContainerId {
  return `branch:${branchId}`;
}

export function isBranchContainer(
  id: StepContainerId,
): id is `branch:${string}` {
  return id !== "root";
}

export function getBranchIdFromContainer(id: StepContainerId): string | null {
  return id === "root" ? null : id.slice("branch:".length);
}

/** Read the steps array for the given container, or null if not found. */
export function getContainerSteps(
  steps: CarePlanStep[],
  container: StepContainerId,
): CarePlanStep[] | null {
  if (container === "root") return steps;
  const branchId = container.slice("branch:".length);
  const found = findBranchInTree(steps, branchId);
  return found ? found.branch.steps : null;
}

/**
 * Locate the container that holds the given step. Walks the tree depth-first,
 * returning either "root" or `branch:<id>` for the closest enclosing container.
 *
 * Used by the drag-over move handler so the source's CURRENT location in
 * state is the source-of-truth — `source.initialGroup` from dnd-kit is only
 * accurate at drag-start, not after a previous dragover already moved the step.
 */
export function findStepContainer(
  steps: CarePlanStep[],
  stepId: string,
): StepContainerId | null {
  if (steps.some((s) => s.id === stepId)) return rootContainer();
  for (const step of steps) {
    if (step.type !== StepType.DECISION) continue;
    for (const branch of step.branches ?? []) {
      if (branch.steps.some((s) => s.id === stepId)) {
        return branchContainer(branch.id);
      }
      const nested = findStepContainer(branch.steps, stepId);
      if (nested) return nested;
    }
  }
  return null;
}

/** Replace the steps array of the given container by applying mutate(). */
export function updateContainerSteps(
  steps: CarePlanStep[],
  container: StepContainerId,
  mutate: (arr: CarePlanStep[]) => CarePlanStep[],
): CarePlanStep[] {
  if (container === "root") return mutate(steps);
  const branchId = container.slice("branch:".length);
  return updateBranchInTree(steps, branchId, (b) => ({
    ...b,
    steps: mutate(b.steps),
  }));
}

/** All branchIds that live anywhere inside this step's branch subtree. */
export function collectDescendantBranchIds(
  step: CarePlanStep,
  ids: Set<string> = new Set(),
): Set<string> {
  for (const branch of step.branches ?? []) {
    ids.add(branch.id);
    for (const s of branch.steps) {
      collectDescendantBranchIds(s, ids);
    }
  }
  return ids;
}

/**
 * Map a timeline-relative insertion index (non-message steps only) to an
 * array index in `steps`. Linked messages stay attached to their parent.
 */
export function findStepInsertIndex(
  steps: CarePlanStep[],
  atTimelineIndex: number,
): number {
  const timeline = steps.filter((s) => !isLinkedMessage(s));
  if (atTimelineIndex <= 0 || timeline.length === 0) return 0;
  if (atTimelineIndex >= timeline.length) return steps.length;

  const stepBefore = timeline[atTimelineIndex - 1];
  let i = steps.findIndex((s) => s.id === stepBefore.id);
  if (i === -1) return steps.length;

  while (
    i + 1 < steps.length &&
    isLinkedMessage(steps[i + 1]) &&
    steps[i + 1].parentStepId === stepBefore.id
  ) {
    i++;
  }
  return i + 1;
}

export interface DragOverMoveInfo {
  sourceId: string;
  /** Target droppable id (string form). Used when target isn't a sortable. */
  targetId?: string;
  /** Sortable target's group accessor — present when target is a sortable. */
  targetGroup?: string;
  /** Sortable target's index accessor — present when target is a sortable. */
  targetIndex?: number;
  /** Cursor (or drag-shape center) coordinate along the layout's main axis —
   * used to bias cross-container insert before/after the target item, matching
   * @dnd-kit's `move()` helper. Caller picks X for horizontal layouts and Y for
   * vertical so the bias works in either orientation. */
  cursorMain?: number;
  /** Target sortable's center coordinate along the same axis as `cursorMain`. */
  targetCenterMain?: number;
}

export interface DragOverMoveParams {
  stepId: string;
  fromContainer: StepContainerId;
  toContainer: StepContainerId;
  toIndex: number;
}

/**
 * Translate a dnd-kit dragover event (already unpacked into source/target
 * info) into the parameters `moveStepBetweenContainers` expects. Returns null
 * when the event can't be turned into a meaningful move (source missing,
 * unknown target, etc.).
 *
 * Resolves `fromContainer` from current state via `findStepContainer` rather
 * than trusting `source.initialGroup` — during an active drag the step may
 * have already been relocated by a previous dragover.
 *
 * For non-sortable droppable targets (the BranchDropZone wrapping an empty
 * branch), `toIndex` is the timeline length of the target container, so the
 * step appends to the end. The same-position no-op short-circuits inside
 * `moveStepBetweenContainers`.
 */
export function resolveDragOverMove(
  steps: CarePlanStep[],
  info: DragOverMoveInfo,
): DragOverMoveParams | null {
  const fromContainer = findStepContainer(steps, info.sourceId);
  if (!fromContainer) return null;

  let toContainer: StepContainerId;
  let toIndex: number;

  if (
    info.targetIndex !== undefined &&
    typeof info.targetGroup === "string" &&
    (info.targetGroup === "root" || info.targetGroup.startsWith("branch:"))
  ) {
    toContainer = info.targetGroup as StepContainerId;
    toIndex = info.targetIndex;
    // Cross-container only: bias above/below the target's center, mirroring
    // @dnd-kit/dom/sortable's `mutate()` (sortable.js:390). Same-container
    // reorders use the raw target index — `findStepInsertIndex` then maps
    // it onto the post-removal array, matching arrayMove semantics.
    if (
      fromContainer !== toContainer &&
      info.cursorMain !== undefined &&
      info.targetCenterMain !== undefined &&
      Math.round(info.cursorMain) > Math.round(info.targetCenterMain)
    ) {
      toIndex += 1;
    }
  } else if (
    info.targetId !== undefined &&
    (info.targetId === "root" || info.targetId.startsWith("branch:"))
  ) {
    toContainer = info.targetId as StepContainerId;
    const arr = getContainerSteps(steps, toContainer) ?? [];
    toIndex = arr.filter((s) => !isLinkedMessage(s)).length;
  } else {
    return null;
  }

  return { stepId: info.sourceId, fromContainer, toContainer, toIndex };
}

/**
 * Move a step (with its linked-message children) between containers in the
 * care-plan tree. Same-container moves reorder; cross-container moves remove
 * from source and insert into target at `toIndex` (a timeline-relative index).
 *
 * Returns the original tree by reference if the move is a no-op (step not
 * found, or the cycle guard rejects dropping a Decision into one of its own
 * descendant branches). The caller is responsible for any post-move
 * recalculation (stepNumber, scheduledDate, etc.).
 */
export function moveStepBetweenContainers(
  steps: CarePlanStep[],
  params: {
    stepId: string;
    fromContainer: StepContainerId;
    toContainer: StepContainerId;
    toIndex: number;
  },
): {
  steps: CarePlanStep[];
  movedToContainer: StepContainerId | null;
  movedFromContainer: StepContainerId | null;
} {
  const { stepId, fromContainer, toContainer, toIndex } = params;

  const sourceArr = getContainerSteps(steps, fromContainer);
  if (!sourceArr)
    return { steps, movedFromContainer: null, movedToContainer: null };

  const stepIdx = sourceArr.findIndex((s) => s.id === stepId);
  if (stepIdx === -1)
    return { steps, movedFromContainer: null, movedToContainer: null };
  const step = sourceArr[stepIdx];

  // Cycle guard: dropping a Decision into one of its own descendant branches
  // would reparent the branch under itself.
  if (step.type === StepType.DECISION && isBranchContainer(toContainer)) {
    const targetBranchId = getBranchIdFromContainer(toContainer)!;
    if (collectDescendantBranchIds(step).has(targetBranchId)) {
      return { steps, movedFromContainer: null, movedToContainer: null };
    }
  }

  // The step's linked-message children travel with it. Filter the source
  // array directly so the block keeps the original relative order — a
  // "before" reminder authored above its parent stays above it after the
  // move.
  const block: CarePlanStep[] = sourceArr.filter(
    (s) =>
      s.id === step.id || (isLinkedMessage(s) && s.parentStepId === step.id),
  );
  const blockIds = new Set(block.map((s) => s.id));

  // Same-container reorder: do remove + insert in a single pass on a fresh
  // array so toIndex is interpreted against the post-removal timeline.
  // Short-circuit when the resolved insert lands the source back at its
  // existing position — dragover fires every frame and we must avoid
  // allocating a new tree (and triggering re-render + recalculation) for
  // hover-in-place events.
  if (fromContainer === toContainer) {
    const without = sourceArr.filter((s) => !blockIds.has(s.id));
    const insertAt = findStepInsertIndex(without, toIndex);
    const currentInsertAt = sourceArr.findIndex((s) => s.id === step.id);
    if (insertAt === currentInsertAt) {
      return { steps, movedFromContainer: null, movedToContainer: null };
    }
    const next = updateContainerSteps(steps, toContainer, () => [
      ...without.slice(0, insertAt),
      ...block,
      ...without.slice(insertAt),
    ]);
    return {
      steps: next,
      movedFromContainer: fromContainer,
      movedToContainer: toContainer,
    };
  }

  // Cross-container: remove from source, then insert into target.
  let next = updateContainerSteps(steps, fromContainer, (arr) =>
    arr.filter((s) => !blockIds.has(s.id)),
  );
  next = updateContainerSteps(next, toContainer, (arr) => {
    const insertAt = findStepInsertIndex(arr, toIndex);
    return [...arr.slice(0, insertAt), ...block, ...arr.slice(insertAt)];
  });

  return {
    steps: next,
    movedFromContainer: fromContainer,
    movedToContainer: toContainer,
  };
}

/**
 * Recursively update a step anywhere in the tree (top-level or inside branches).
 */
export function updateStepInTree<T extends CarePlanStep>(
  steps: T[],
  stepId: string,
  updates: Partial<Omit<CarePlanStep, "kind">>,
): T[] {
  let changed = false;
  const result = steps.map((step) => {
    if (step.id === stepId) {
      changed = true;
      return { ...step, ...updates } as T;
    }
    if (step.type === StepType.DECISION) {
      const branches = step.branches ?? [];
      let branchesChanged = false;
      const updatedBranches = branches.map((branch) => {
        const updatedSteps = updateStepInTree(branch.steps, stepId, updates);
        if (updatedSteps !== branch.steps) {
          branchesChanged = true;
          return { ...branch, steps: updatedSteps };
        }
        return branch;
      });
      if (branchesChanged) {
        changed = true;
        return { ...step, branches: updatedBranches } as T;
      }
    }
    return step;
  });
  return changed ? result : steps;
}

// ============================================================================
// Configuration
// ============================================================================

export type Orientation = "vertical" | "horizontal";
export type DetailMode = "sidebar" | "popover";

// ============================================================================
// Context
// ============================================================================

export interface CarePlanState {
  steps: CarePlanStep[];
  /** Steps filtered by onlyShowCurrentPasses — use this for rendering timelines */
  visibleSteps: CarePlanStep[];
  selectedStepId: string | null;
  selectedStep: CarePlanStep | null;
  expandedStepId: string | null;
}

export interface CarePlanActions {
  addStep: (type?: StepType) => void;
  duplicateStep: (step: CarePlanStep, atTimelineIndex: number) => void;
  updateStep: (
    id: string,
    updates: Partial<Omit<PatientCarePlanStep, "kind">>,
  ) => void;
  removeStep: (id: string) => void;
  selectStep: (id: string | null) => void;
  moveStep: (fromIndex: number, toIndex: number) => void;
  toggleStepExpanded: (id: string) => void;
  selectBranch?: (decisionStepId: string, branchId: string) => Promise<void>;
  addBranch: (decisionStepId: string) => void;
  removeBranch: (decisionStepId: string, branchId: string) => void;
  updateBranch: (
    branchId: string,
    updates: Partial<Pick<CarePlanBranch, "name" | "tagIds">>,
  ) => void;
  addBranchStep: (branchId: string, type: StepType) => void;
  updateBranchStep: (
    branchId: string,
    stepId: string,
    updates: Partial<Omit<PatientCarePlanStep, "kind">>,
  ) => void;
  removeBranchStep: (branchId: string, stepId: string) => void;
  duplicateBranchStep: (
    branchId: string,
    step: CarePlanStep,
    atIndex: number,
  ) => void;
  moveBranchStep: (
    branchId: string,
    fromIndex: number,
    toIndex: number,
  ) => void;
  /**
   * Unified drag-and-drop move: move a step between any two containers
   * (root or any branch) at a timeline-relative index. Same-container moves
   * are reorders; cross-container moves transplant the step (and its linked
   * messages) into the target container.
   */
  moveStepTo: (params: {
    stepId: string;
    fromContainer: StepContainerId;
    toContainer: StepContainerId;
    toIndex: number;
  }) => void;
  /**
   * Apply a mid-drag move from a dnd-kit dragover event. Resolves the
   * source's current container against fresh state, then runs the same
   * mutation + recalc as `moveStepTo`. Idempotent: a no-op move returns the
   * existing state by reference, so it's safe to fire on every dragover.
   */
  applyDragOverMove: (info: DragOverMoveInfo) => void;
  /**
   * Restore the steps array to a previous snapshot. Used by the DnD layer
   * to roll back optimistic mutations made during a drag that the user
   * cancels (Esc / drop-outside).
   */
  restoreSteps: (snapshot: CarePlanStep[]) => void;
}

export type CarePlanSize = "sm" | "md";

export interface CarePlanConfig {
  orientation: Orientation;
  detailMode: DetailMode;
  readOnly: boolean;
  isCreating: boolean;
  enableMessages: boolean;
  size: CarePlanSize;
}

export interface CarePlanLookups {
  services: ServiceDetail[];
  medicationOptions: MultiSelectOption[];
  measurementOptions: MultiSelectOption[];
  /** When set (patient care plan), suggestion queries can return real PATIENT values. */
  patientId?: string;
  serviceById: Map<string, ServiceDetail>;
  medicationById: Map<string, MultiSelectOption>;
  measurementById: Map<string, MultiSelectOption>;
  /** True while services or medications are still being fetched */
  isLookupsLoading: boolean;
  lookupError?: Error | null;
  /** When provided, appointment-type steps show a "Schedule" action */
  onScheduleStep?: (step: CarePlanStep) => void;
  /**
   * IDs of appointment steps that can be scheduled right now — the next
   * PENDING appointment on the active path, plus the first step of each
   * branch under a live (unresolved, unblocked) DECISION. Scheduling a
   * branch-first step auto-selects that branch on the backend.
   */
  schedulableStepIds: Set<string>;
}

export interface CarePlanEditor {
  id?: string;
  idLabel?: string;
  name: string;
  onNameChange: (value: string) => void;
  namePlaceholder?: string;
  nameAutoFocus?: boolean;
  nameError?: string;
  onDelete?: () => void;
  isDeleting?: boolean;
  description: string;
  onDescriptionChange: (value: string) => void;
  descriptionInput?: React.ReactNode;
  headerRight?: React.ReactNode;
  selectedTagIds: string[];
  onTagsChange: (ids: string[]) => void;
  onTagSavingChange: (saving: boolean) => void;
  tagError?: boolean;
  tagErrorMessage?: string;
}

export type CycleCreationPhase =
  | "selecting-start"
  | "selecting-end"
  | "configuring";

export interface CycleCreationState {
  phase: CycleCreationPhase;
  startStepIndex: number | null;
  startStepId: string | null;
  endStepIndex: number | null;
  endStepId: string | null;
  /** Branch ID if creating a cycle inside a branch, null for top-level */
  branchId: string | null;
}

/**
 * State for the "copy sequences across steps" mode. Active while the user
 * is picking destination steps to clone the source step's `sequences` onto.
 * Cross-branch picks are allowed; materialized cycle clones are filtered
 * out as targets (the backend propagates the edit to sibling passes via
 * `reconcile_cycled` when the source step is saved).
 */
export interface SequenceCopyState {
  sourceStepId: string;
  /** Selected destination step IDs (excludes the source). */
  targetStepIds: string[];
}

export interface CarePlanSequenceCopyState {
  state: SequenceCopyState | null;
  /** Begin copy mode rooted at `sourceStepId`. */
  start: (sourceStepId: string) => void;
  /** Add or remove a step from the destination set. */
  toggleTarget: (stepId: string) => void;
  /** Clone the source's sequences onto each selected destination. */
  apply: () => void;
  /** Exit copy mode without applying. */
  cancel: () => void;
}

export interface CarePlanCyclesState {
  cycles: CarePlanCycleDetail[];
  createCycleOpen: boolean;
  openCreateCycle: () => void;
  closeCreateCycle: () => void;
  onCycleCreated?: (config: {
    stepRange: [string, string];
    stepIds: string[];
    passCount: number;
    gapMinutes: number;
    parentCycleId?: string | null;
    childCycleIds?: string[];
    branchId?: string | null;
  }) => void;
  onRenameCycle?: (cycleId: string, name: string) => void;
  /** Inline cycle creation state — null when not creating */
  cycleCreation: CycleCreationState | null;
  /** Called when a step card is clicked during cycle creation */
  selectCycleCreationStep: (stepId: string) => void;
  /** Cancel cycle creation mode */
  cancelCycleCreation: () => void;
  /** Confirm cycle with metadata after both steps are selected */
  confirmCycleCreation: (passCount: number, gapMinutes: number) => void;
  /** Original (non-materialized) steps used for cycle step selection */
  originalSteps: CarePlanStep[];
  /** Cycle ID currently highlighted in the progress panel (null = none) */
  highlightedCycleId: string | null;
  /** Toggle which cycle is highlighted */
  setHighlightedCycleId: (cycleId: string | null) => void;
  /** When true, completed passes are hidden from cycle groups */
  onlyShowCurrentPasses: boolean;
  /** Toggle onlyShowCurrentPasses */
  toggleOnlyShowCurrentPasses: () => void;
  /** Maps cycleId → color index for consistent color lookups */
  cycleColorMap: Map<string, number>;
  /** For child cycles, maps cycleId → which pass of the parent it represents */
  cycleToParentPass: Map<string, number>;
  /** Deduplicated cycles — one representative per logical cycle family */
  displayCycles: CarePlanCycleDetail[];
  /** Map from any cycle ID to the set of all IDs in its family (clone group) */
  cycleFamilyMap: Map<string, Set<string>>;
}

export interface CarePlanSaveState<TCreateInput = void> {
  /** Save changes to an existing care plan (editor mode) */
  save?: () => Promise<void>;
  /** Create a new care plan (creator mode) — returns true on success */
  create?: (input: TCreateInput) => Promise<boolean>;
  hasChanges: boolean;
  isSaving: boolean;
  errors: Record<string, string[] | undefined>;
  /** First validation error message, if any */
  validationError?: string;
}

export interface CarePlanContextType {
  state: CarePlanState;
  actions: CarePlanActions;
  config: CarePlanConfig;
  lookups: CarePlanLookups;
  cycles: CarePlanCyclesState;
  sequenceCopy: CarePlanSequenceCopyState;
  editor?: CarePlanEditor;
  // `any` at the context boundary: each concrete creator (template, patient
  // create, patient edit) publishes a `CarePlanSaveState` with its own
  // specific input shape, and consumers that know the mode narrow it.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saveState?: CarePlanSaveState<any>;
  getNextStep: (
    steps: CarePlanStep[],
    index: number,
  ) => CarePlanStep | undefined;
}
